import ResponsiveWrapper from "../ResponsiveWrapper/ResponsiveWrapper";
import { Pie } from "@visx/shape";
import { Group } from "@visx/group";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { formatCurrency } from "../../utils/format";
import {
	getCategoryColor,
	getIncomeSubCategoryColor,
} from "../../utils/categoryColors";
import { useScreenHeight, getChartHeight } from "../../hooks/useScreenHeight";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";

const PieChart = ({ data, translate }) => {
	const { locale } = useUserPreferences();
	const screenHeight = useScreenHeight();
	const chartHeight = getChartHeight(screenHeight);

	// Tooltip setup
	const {
		tooltipData,
		tooltipLeft,
		tooltipTop,
		tooltipOpen,
		showTooltip,
		hideTooltip,
	} = useTooltip();
	const { containerRef, TooltipInPortal } = useTooltipInPortal({
		scroll: true,
	});

	const renderChart = ({ width, height }) => {
		if (width === 0 || height === 0) return null;

		// Determines if the current screen is considered "mobile" (small width devices)
		const isMobile = width <= 640;

		// Sets the margin around the pie chart depending on device type
		const margin = isMobile ? 30 : 20;

		// Calculates the outer radius of the pie chart (half the smaller dimension, minus margin)
		const radius = Math.min(width, height) / 2 - margin;

		// Variables that will store the chart center coordinates
		let centerX, centerY;

		if (isMobile) {
			// On mobile: center horizontally in the available width
			centerX = width / 2;

			// On mobile: place the chart higher on the screen (not exactly centered vertically),
			// but never lower than radius + 50 to avoid cutting off
			centerY = Math.min(height * 0.4, radius + 50);
		} else {
			// Width reserved for the legend section (on the right of the chart in desktop view)
			const legendWidth = 220;

			// Remaining space for the chart after removing legend and padding (32px)
			const availableWidth = width - legendWidth - 32;

			// Ensures the pie chart is centered in the available space,
			// and pushed a bit to the right if necessary to avoid overlapping with legend
			centerX = Math.max(availableWidth / 2, radius + 40);

			// On desktop: vertically centered in the container
			centerY = height / 2;
		}

		return (
			<>
				<svg width={width} height={height}>
					<Group top={centerY} left={centerX}>
						<Pie
							data={data}
							pieValue={d => d.value}
							outerRadius={radius}
							innerRadius={radius * 0.5}
							cornerRadius={3}
							padAngle={0.005} // Slice separator width
						>
							{pie => {
								return pie.arcs.map((arc, i) => {
									const path = pie.path(arc);
									const [centroidX, centroidY] = pie.path.centroid(arc);

									// Determine color based on data type
									let color;
									if (arc.data.category) {
										color = getCategoryColor(arc.data.category);
									} else if (arc.data.subCategory) {
										color = getIncomeSubCategoryColor(arc.data.subCategory);
									} else {
										color = `hsl(${(i * 360) / data.length}, 70%, 60%)`;
									}

									// Prepare tooltip label
									const tooltipLabel = arc.data.category
										? translate(`category.${arc.data.category}`)
										: arc.data.subCategory
										? translate(`subCategory.income.${arc.data.subCategory}`)
										: arc.data.label;

									return (
										<g key={`arc-${i}`}>
											<path
												d={path}
												fill={color}
												onMouseMove={e => {
													const event = e.nativeEvent;
													showTooltip({
														tooltipLeft: event.offsetX,
														tooltipTop: event.offsetY,
														tooltipData: {
															label: tooltipLabel,
															value: arc.data.value,
															percentage: arc.data.percentage,
															category: arc.data.category,
															subCategory: arc.data.subCategory,
															isIncome: !!arc.data.subCategory,
														},
													});
												}}
												onMouseLeave={hideTooltip}
												style={{ cursor: "pointer" }}
											/>
											<text
												x={centroidX}
												y={centroidY}
												dy=".33em"
												fill="#fff"
												fontSize={11}
												textAnchor="middle"
												fontWeight="bold"
												style={{ pointerEvents: "none" }}
											>
												{arc.data.percentage}%
											</text>
										</g>
									);
								});
							}}
						</Pie>
					</Group>
				</svg>

				<div className="pie-legend">
					{data.map((item, index) => {
						// Determine color for legend
						let color;
						if (item.category) {
							color = getCategoryColor(item.category);
						} else if (item.subCategory) {
							color = getIncomeSubCategoryColor(item.subCategory);
						} else {
							color = `hsl(${(index * 360) / data.length}, 70%, 60%)`;
						}

						return (
							<div
								key={item.category || item.subCategory || index}
								className="legend-item"
							>
								<div
									className="legend-color"
									style={{ backgroundColor: color }}
								/>
								<span>
									{item.category
										? translate(`category.${item.category}`)
										: item.subCategory
										? translate(`subCategory.income.${item.subCategory}`)
										: item.label}{" "}
									({formatCurrency(item.value, locale)})
								</span>
							</div>
						);
					})}
				</div>
			</>
		);
	};

	return (
		<div
			className="chart-box"
			style={{ height: `${chartHeight}px` }}
			ref={containerRef}
		>
			<ResponsiveWrapper fallbackHeight={chartHeight}>
				{renderChart}
			</ResponsiveWrapper>
			{tooltipOpen && tooltipData && (
				<TooltipInPortal
					top={tooltipTop}
					left={tooltipLeft}
					className="chart-tooltip"
				>
					<div
						className="tooltip-header"
						style={{
							color: tooltipData.category
								? getCategoryColor(tooltipData.category)
								: tooltipData.subCategory
								? getIncomeSubCategoryColor(tooltipData.subCategory)
								: "var(--text-color)",
						}}
					>
						{tooltipData.isIncome
							? translate("category.income")
							: tooltipData.category
							? translate(`category.${tooltipData.category}`)
							: tooltipData.subCategory
							? translate(`subCategory.income.${tooltipData.subCategory}`)
							: tooltipData.label}
					</div>
					{tooltipData.isIncome && (
						<div className="tooltip-label">{tooltipData.label}</div>
					)}
					<div className="tooltip-value">
						{formatCurrency(tooltipData.value, locale)}
					</div>
				</TooltipInPortal>
			)}
		</div>
	);
};

export default PieChart;
